import { Button, CircularProgress, Grid, Paper } from "@material-ui/core";
import { useMemo, useState } from "react";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import { Building, Contact, ContactBuilding } from "../../../../graphql/models";
import { useSnackbarMutation } from "../../../../hooks";
import { i18nForFields } from "../../../../i18n";
import {
  ConfirmButtonWithDialog,
  GqlRecordPicker,
  I18NTextField,
  PhoneNumbersPicker,
} from "../../../components";
import { EmailsPicker } from "../../../components/emails_picker";

import useContactFormStyles from "./contacts_form.style";

export interface ContactEditFormProps {
  value: Contact | undefined;
  onValueDeleted: () => void;
  onValueUpdatedSuccessfully: (v: Contact) => void;
}

export function ContactEditForm(props: ContactEditFormProps) {
  const { value, onValueDeleted, onValueUpdatedSuccessfully } = props;
  const classes = useContactFormStyles();

  let mutation = MUTATIONS.CREATE_CONTACT;
  if (value && value?.id) {
    mutation = MUTATIONS.UPDATE_CONTACT;
  }

  const [invokeCreateOrUpdate, { loading }] = useSnackbarMutation(mutation, {
    onCompleted: (_) => {
      // Todo: this is slightly bad.
      onValueUpdatedSuccessfully(value as Contact);
    },
  });

  const [invokeDeleteContact, { loading: deletingContact }] =
    useSnackbarMutation(MUTATIONS.DELETE_CONTACT, {
      onCompleted: onValueDeleted,
    });

  const isLoading = useMemo(
    () => loading || deletingContact,
    [deletingContact, loading]
  );

  // const [label, setLabel] = useState<string | undefined>(value?.label);
  // const [description, setDescription] = useState<string | undefined>(
  //   value?.description
  // );
  const [i18nFields, setI18nFields] = useState<
    Partial<i18nForFields<"label" | "description">>
  >(value?.i18n ?? {});
  const [buildings, setBuildings] = useState<ContactBuilding[]>(
    value?.buildings ?? []
  );
  const [emailAddresses, setEmailAddresses] = useState<string[]>(
    value?.emailAddresses ?? []
  );
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>(
    value?.phoneNumbers ?? []
  );

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdate({
      variables: {
        id: (value as Contact)?.id,
        contactData: {
          id: (value as Contact)?.id,
          buildings,
          emailAddresses,
          phoneNumbers,
          i18n: i18nFields,
        },
      },
    });
  };

  const triggerInvokeDeleteContact = () => {
    invokeDeleteContact({
      variables: {
        id: value?.id,
      },
    });
  };

  return (
    <form
      className={classes.root}
      onSubmit={onFormSubmit}
      noValidate
      autoComplete="off"
    >
      <Grid container spacing={3}>
        <Grid item sm={4} xs={12}>
          <I18NTextField
            required
            i18nFieldName="label"
            disabled={isLoading}
            className={classes.textField}
            label="Nome"
            variant="outlined"
            value={i18nFields}
            onChange={(e) => setI18nFields(e)}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <I18NTextField
            required
            i18nFieldName="description"
            disabled={isLoading}
            className={classes.textField}
            label="Descrizione"
            variant="outlined"
            value={i18nFields}
            onChange={(e) => setI18nFields(e)}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <GqlRecordPicker
            className={classes.textField}
            multiple={true}
            value={buildings}
            getOptionLabel={(record: Building) => record.code ?? record.name}
            placeHolder={"Palazzi"}
            renderOption={(record: Building) => record.code ?? record.name}
            onValueChange={(value) =>
              setBuildings(
                value.map((b) => ({
                  id: b.id,
                  code: b.code,
                }))
              )
            }
            query={QUERIES.BUILDINGS("full")}
            queryResultDataField={"buildings"}
          />
        </Grid>
        <Grid item sm={12} xs={12}>
          <Paper elevation={3}>
            <Grid container>
              <Grid item sm={6} xs={12}>
                <PhoneNumbersPicker
                  value={phoneNumbers}
                  onChange={setPhoneNumbers}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <EmailsPicker
                  value={emailAddresses}
                  onChange={setEmailAddresses}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item sm={12} xs={12}>
          <Button
            className={classes.textField}
            type="submit"
            variant="contained"
            color="primary"
          >
            {!isLoading ? "Salva" : <CircularProgress />}
          </Button>
        </Grid>
        {value?.id && (
          <Grid item sm={12} xs={12}>
            <ConfirmButtonWithDialog
              className={classes.textField}
              type="button"
              variant="contained"
              color="secondary"
              dialogProps={{
                fullScreen: false,
                title: "Conferma eliminazione",
              }}
              dialogContent={
                "Confermi di voler eliminare definitivamente questo elemento? l'operazione è irreversibile."
              }
              onConfirm={() => triggerInvokeDeleteContact()}
              onUndo={() => void 0}
            >
              {!isLoading ? "Elimina" : <CircularProgress />}
            </ConfirmButtonWithDialog>
          </Grid>
        )}
      </Grid>
    </form>
  );
}
